import { useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import LoginSheet from "../../../components/signup/LoginSheet";
import {
  gaEventSignupSheetOpened,
  gaEventSignupSheetDismissed,
} from "../../../tools/analytics/domains/onboarding.analytics";
import { memorizePostLoginRedirectPath } from "../../app/utils/postLoginRedirect";
import { useSignupSheet } from "./useSignupSheet";

// Scroll distance (px) past which the signup invitation surfaces. The visitor
// gets to read a good chunk of the shared conversation first, then the sheet
// nudges them to sign up.
const SCROLL_TRIGGER_PX = 600;

// Self-contained signup invitation used on surfaces open to anonymous visitors
// (the shared-conversation read-only view). It owns the LoginSheet and a
// one-shot scroll trigger; the sheet's own state and auth handlers come from
// `useSignupSheet`, which every host of the sheet shares.
//
// Returned as a hook (not inlined in the page) because it carries UI state —
// the business page stays free of useState/useRef. `handleScroll` opens the
// sheet once per mount when the reader scrolls past the threshold; `open` lets
// the page surface it on an explicit interaction (composer tap).
//
// `enabled` is false for already-authenticated viewers — there is nothing to
// sign up for, so the sheet must never surface (neither on scroll nor on open).
export function useScrollTriggeredSignupInvite(enabled: boolean): {
  sheet: React.ReactElement;
  handleScroll: (scrollTop: number) => void;
  open: () => void;
} {
  const history = useHistory();
  const location = useLocation();
  const hadTypedEmailRef = useRef(false);
  // Once the sheet has auto-opened (or been dismissed) this mount, never
  // auto-open it again on further scrolling.
  const autoTriggeredRef = useRef(false);

  // A provider login keeps this page under its popup and lands back on it. An
  // existing address leaves for `/login`, so the page rides the memorized path.
  const goToLogin = useCallback(
    (email: string) => {
      memorizePostLoginRedirectPath();
      history.push({ pathname: "/login", state: { email } });
    },
    [history]
  );

  const signup = useSignupSheet({
    surface: "bottom_sheet",
    initiallyOpen: false,
    onExistingAccount: goToLogin,
    returnTo: location.pathname + location.search,
  });

  const open = useCallback(() => {
    if (!enabled) return;
    gaEventSignupSheetOpened({
      surface: "bottom_sheet",
      is_invited_to_room: false,
    });
    signup.open();
  }, [enabled, signup]);

  const handleScroll = useCallback(
    (scrollTop: number) => {
      if (!enabled || autoTriggeredRef.current || signup.isOpen) return;
      if (scrollTop < SCROLL_TRIGGER_PX) return;
      autoTriggeredRef.current = true;
      open();
    },
    [enabled, signup.isOpen, open]
  );

  const handleClose = useCallback(() => {
    autoTriggeredRef.current = true;
    gaEventSignupSheetDismissed({ had_typed_email: hadTypedEmailRef.current });
    signup.close();
  }, [signup]);

  const sheet = (
    <LoginSheet
      isOpen={signup.isOpen}
      onClose={handleClose}
      onOAuth={signup.handleOAuth}
      onEmailSubmit={signup.handleEmailSubmit}
      onEmailFirstTyped={() => {
        hadTypedEmailRef.current = true;
      }}
      onQRCodeScan={null}
      invitedRoomLogoUrl={null}
      // The shared-conversation page has no legal line of its own, and this
      // sheet is the only door to an account on it.
      displaysLegalNotice
      disabled={signup.isSigningIn}
    />
  );

  return { sheet, handleScroll, open };
}
